/*
 * Code heavily influenced by Segment's Store https://github.com/segmentio/localstorage-retry/blob/master/lib/store.js
 * and Engine https://github.com/segmentio/localstorage-retry/blob/master/lib/engine.js
 * But is not used because it's not public API and is too restrictive in the way it sets keys and does JSON parsing and stringify
 */

import { envType } from './envType';
import InMemoryStorage from './InMemoryStorage';
import { isSupported } from './isSupported';
import { type Options } from './types';

export const AWC_STORAGE_PREFIX = 'awc';

export const GLOBAL_IN_MEMORY_NAME = 'awcInMemoryStorageFallback';

export default class SafeStorage {
	private prefix: string;
	private store!: Storage;

	options: Options;

	constructor(storageToUse: Storage, options: Options = {}) {
		this.options = options;

		const prefixes: string[] = [];
		if (options.useStoragePrefix === undefined || options.useStoragePrefix) {
			prefixes.push(AWC_STORAGE_PREFIX);
		}
		if (options.envPrefix && options.envPrefix !== envType.PROD) {
			prefixes.push(options.envPrefix);
		}

		this.prefix = prefixes.join('-');

		if (isSupported(storageToUse)) {
			this.store = storageToUse;
		} else {
			this.swapToInMemory();
		}
	}

	getStore = (): Storage => this.store;

	getItem = (key: string): string | null => this.store.getItem(this.createKey(key));

	removeItem = (key: string): void => this.store.removeItem(this.createKey(key));

	setItem = (key: string, value: string): void => {
		try {
			this.store.setItem(this.createKey(key), value);
		} catch (err) {
			// Theoretically should only be QuotaExceededError, but keeping 'err' generic
			// https://developer.mozilla.org/en-US/docs/Web/API/Storage/setItem
			this.swapToInMemory();
			this.store.setItem(this.createKey(key), value);
		}
	};

	swapToInMemory = (): void => {
		if (this.store === InMemoryStorage || typeof window === 'undefined') {
			return;
		}

		const keys: string[] = Object.keys(this.store || {});

		let inMemoryStorage: Storage;

		if (isSupported(window[GLOBAL_IN_MEMORY_NAME])) {
			inMemoryStorage = window[GLOBAL_IN_MEMORY_NAME];
		} else {
			inMemoryStorage = InMemoryStorage;
			if (!window[GLOBAL_IN_MEMORY_NAME]) {
				// If not already attached to window then attach our instance of InMemoryStorage
				// We need to do this because if there are multiple instances of Analytics Client in multiple scopes,
				// there is no way to guarantee the throttling behaviour of the UI viewed event across those instances without a global singleton
				window[GLOBAL_IN_MEMORY_NAME] = InMemoryStorage;
			}
		}

		keys.forEach((key: string) => {
			if (key.indexOf(this.getPrefix()) === 0) {
				const value = this.store.getItem(key);
				if (value) {
					inMemoryStorage.setItem(key, value);
				}
			}
		});

		this.store = inMemoryStorage;
	};

	clear = (): void => this.store.clear();

	key = (index: number): string | null => this.store.key(index);

	getPrefix = (): string => this.prefix;

	createKey = (key: string): string => {
		if (this.prefix) {
			return `${this.getPrefix()}.${key}`;
		}
		return key;
	};
}

declare global {
	interface Window {
		[GLOBAL_IN_MEMORY_NAME]: any;
	}
}
